import type { Chunk, FindChunks } from 'react-highlight-words'
import type { RecentSearches } from '../../interfaces'
import { isEmpty } from 'lodash'

export const mediaCollectionNameRegex = /^(?!.*\/).*$/u

// Helper functions for recent searches

const MAX_SEARCHES = 15
const STORAGE_KEY = 'recentSearches'
const COLLECTION_STORAGE_KEY = 'recentSearchesForCollections'

// Helper function to get recent searches from local storage
const getRecentSearches = (storageKey: string): RecentSearches => {
  const storedSearches = localStorage.getItem(storageKey)
  return storedSearches ? (JSON.parse(storedSearches) as RecentSearches) : {}
}

// Helper function to save recent searches to local storage
const saveRecentSearches = (
  recentSearches: RecentSearches,
  storageKey: string,
): void => {
  localStorage.setItem(storageKey, JSON.stringify(recentSearches))
}

// Function to get recent searches in descending order of recency
export const getRecentSearchesDescending = (
  forMediaCollection = false,
): RecentSearches => {
  const storageKey = forMediaCollection ? COLLECTION_STORAGE_KEY : STORAGE_KEY
  const recentSearches: RecentSearches = getRecentSearches(storageKey)

  const b = Object.entries(recentSearches)
    .sort(([, timestampA], [, timestampB]) => timestampB - timestampA)
    .reduce((acc, [s, t]) => ({ ...acc, [s]: t }), {})
  return b
}

// Function to insert a recent search
export const insertRecentSearch = (
  search: string,
  forMediaCollection = false,
): void => {
  if (!search) {
    return
  }
  const storageKey = forMediaCollection ? COLLECTION_STORAGE_KEY : STORAGE_KEY
  const recentSearches: RecentSearches = getRecentSearches(storageKey)

  // Add or update the timestamp for the search
  recentSearches[search] = Date.now()

  // Keep only the latest MAX_SEARCHES searches
  const sortedSearches = Object.entries(recentSearches)
    .sort(([, timestampA], [, timestampB]) => timestampB - timestampA)
    .slice(0, MAX_SEARCHES)
    .reduce((acc, [s, t]) => ({ ...acc, [s]: t }), {})

  saveRecentSearches(sortedSearches, storageKey)
}

// Helper function for highlighting search terms
export const findChunksAtBeginningOfWords = ({
  autoEscape,
  caseSensitive,
  sanitize,
  searchWords,
  textToHighlight,
}: FindChunks): Chunk[] => {
  const chunks: Chunk[] = []
  const textLow = textToHighlight.toLowerCase()

  // Split on every non-word character
  // eslint-disable-next-line require-unicode-regexp
  const sep = /[\W_.]+/

  const splittedSearchWordsArr = searchWords.map(sw =>
    (sw as string).split(sep),
  )

  searchWords = splittedSearchWordsArr.flat()

  searchWords = searchWords.filter(s => !isEmpty(s))

  const singleTextWords = textLow.split(sep)

  let fromIndex = 0
  const singleTextWordsWithPos = singleTextWords.map(s => {
    const indexInWord = textLow.indexOf(s, fromIndex)
    fromIndex = indexInWord + s.length
    return {
      word: s,
      index: indexInWord,
    }
  })

  // Add chunks for every searchWord
  searchWords.forEach(sw => {
    const swLow = (sw as string).toLowerCase()
    // Do it for every single text word
    singleTextWordsWithPos.forEach(s => {
      if (s.word.startsWith(swLow)) {
        const start = s.index
        const end = s.index + swLow.length
        chunks.push({
          start,
          end,
        })
      }
    })
  })

  return chunks
}
